import { useState } from "react";
import SidebarItem from "./SidebarItem";
import type { SidebarProps } from "../../../types/sidebar";
import { SidebarContext } from "../../../contexts/SidebarContext";
import Logo from "../../ui/Logo";
import BuyCreditsWidget from "./BuyCreditsWidget";
import ThemeToggle from "./ThemeToggle";
import { useAuth } from "../../../hooks/useAuth";

export default function Sidebar({
    isOpen,
    toggleSidebar,
    mainMenu = [],
    theme = {},
    expandedWidth = "w-64",
    collapsedWidth = "w-20",
}: SidebarProps) {
    const { user } = useAuth();
    const [isHovered, setIsHovered] = useState(false);
    
    // Auto-expand on hover
    const expanded = isHovered;

    const finalTheme = {
        bgColor: "bg-white",
        borderColor: "border-gray-200",
        hoverBg: "hover:bg-gray-50",
        activeColor: "text-violet-600",
        activeBg: "bg-violet-100",
        ...theme,
    };

    return (
        <>
            {/* Mobile overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 md:hidden z-20 backdrop-blur-sm transition-all"
                    onClick={toggleSidebar}
                />
            )}

            <aside
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={`fixed md:static z-30 h-screen max-h-screen ${finalTheme.bgColor} border-r ${finalTheme.borderColor}
                transition-all duration-300 ease-in-out shadow-sm
                ${isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
                ${expanded ? expandedWidth : collapsedWidth} `}>
                
                <nav className="flex flex-col h-full">
                    {/* HEADER */}
                    <div
                        className={`flex h-20 items-center border-b ${finalTheme.borderColor} px-6 transition-all`}>
                        <Logo iconOnly={!expanded} className="w-full" />
                    </div>

                    <SidebarContext.Provider value={{ expanded, theme: finalTheme }}>
                        {/* MAIN MENU */}
                        <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar py-6">
                            {mainMenu.length > 0 && (
                                <ul className="px-4 space-y-1.5">
                                    {mainMenu.map((item) => (
                                        <SidebarItem
                                            key={item.path}
                                            icon={item.icon}
                                            text={item.name}
                                            path={item.path}
                                            end={item.end}
                                        />
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* THEME TOGGLE */}
                        <div className={`px-4 ${expanded ? "" : "px-3"} pb-2`}>
                            <ThemeToggle expanded={expanded} />
                        </div>

                        {/* PREMIUM CREDITS WIDGET - THE "CRAZY DESIGN" ONE - HIDDEN FOR ADMINS */}
                        {!user?.isAdmin && <BuyCreditsWidget expanded={expanded} />}
                    </SidebarContext.Provider>
                </nav>
            </aside>
        </>
    );
}
